import classNames from 'classnames';
import React, { CSSProperties, forwardRef, ReactNode } from 'react';

import '../../icons';
import styles from './HorizontalNavigationItem.module.css';
import { Icon } from '../Icon';

export interface HorizontalNavigationItemOption {
  href: string;
  icon?: string;
  label: string;
  onClick?: React.EventHandler<React.SyntheticEvent>;
  selected?: boolean;
}

export interface HorizontalNavigationItemProps {
  /**
   * Aria label for the item.
   */
  ariaLabel?: string;
  /**
   * Whether the item is disabled or not. Disables the item if true. Defaults to false.
   * If the item is disabled, it will not be clickable.
   */
  disabled?: boolean;
  /**
   * Determines if the dropdown of item, if applicable, is open.
   */
  dropdownOpen?: boolean;
  /**
   * If the tab is considered "home". Slightly alters styling. Defaults to false. Only one home should be specified per Horizontal navigation.
   */
  home?: boolean;
  /**
   * Destination of the link. Does nothing if the {options} prop is specified.
   */
  href?: string;
  /**
   * Optional icon displayed in the item.
   */
  icon?: string;
  /**
   * Label text displayed in the item.
   */
  label?: string;
  /**
   * OnClick event handler for the links. Unused if the Options prop is specified.
   */
  onClick?: React.EventHandler<React.SyntheticEvent>;
  /**
   * Callback for when the dropdown is closed. Does nothing if the {options} prop is not specified.
   */
  onCloseDropdown?: () => void;
  /**
   * Callback for when the dropdown is opened. Does nothing if the {options} prop is not specified.
   */
  onOpenDropdown?: () => void;
  /**
   * Options that appear as a dropdown menu if the item is clicked. Use with `<HorizontalNavigationSelectItem />`
   */
  options?: ReactNode[];
  /**
   * Whether the item is currently selected or not. Applies a different styling. Defaults to false.
   */
  selected?: boolean;
  /**
   * Optional inline styles to add to the container element.
   * Do not alter the look of the component! Should be used only for sizing and positioning.
   */
  style?: CSSProperties;
  /**
   * Optional tooltip text displayed on hover.
   */
  tooltip?: string;
}

export const HorizontalNavigationItem = forwardRef<
  HTMLLIElement,
  HorizontalNavigationItemProps
>(
  (
    {
      ariaLabel,
      disabled = false,
      dropdownOpen = false,
      home = false,
      href,
      icon,
      label,
      onClick,
      onCloseDropdown,
      onOpenDropdown,
      options,
      selected = false,
      style,
      tooltip,
    }: HorizontalNavigationItemProps,
    ref,
  ) => {
    const isDropdown = !!options;
    const keyPressHandler = (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft': {
          e.preventDefault();
          const previousNavigationItem = e.currentTarget
            ?.closest('li')
            ?.previousElementSibling?.querySelector('a, button');
          (previousNavigationItem as HTMLElement)?.focus();
          break;
        }

        case 'ArrowRight': {
          e.preventDefault();
          const nextNavigationItem = e.currentTarget
            .closest('li')
            ?.nextElementSibling?.querySelector('a, button');
          (nextNavigationItem as HTMLElement)?.focus();
          break;
        }

        case 'ArrowDown': {
          e.preventDefault();

          if (isDropdown) {
            onOpenDropdown?.();
          }
          const firstDropdownItem = e.currentTarget
            ?.closest('div[aria-expanded=true]')
            ?.nextElementSibling?.querySelector('a, button');
          (firstDropdownItem as HTMLElement)?.focus();
          break;
        }
        case 'Enter':
          onClick?.(e);
          break;
        case 'Escape':
          e.preventDefault();

          if (dropdownOpen && e.currentTarget.parentElement?.role === 'treeitem') {
            (e.target as HTMLElement)?.blur();
          }

          onCloseDropdown?.();
          break;
        default:
          break;
      }
    };
    const innerContent = (
      <>
        {icon !== undefined && (
          <div className={styles['icon-container']}>
            <Icon
              aria-hidden="true"
              name={icon}
              role="presentation"
            />
          </div>
        )}
        <span className={`fd-navigation__text ${styles['text-container']}`}>{label}</span>
        <span
          aria-hidden="true"
          className="fd-navigation__selection-indicator"
          role="presentation"
        />
      </>
    );

    return (
      <li
        className={classNames(
          `fd-navigation__list-item`,
          home && 'fd-navigation__list-item--home',
          isDropdown && 'fd-popover',
        )}
        key={label}
        ref={ref}
        role="none"
        style={style}
        tabIndex={-1}
      >
        <div
          aria-current={selected ? 'page' : 'false'}
          aria-expanded={dropdownOpen}
          aria-selected={selected}
          className={classNames(
            'fd-navigation__item',
            isDropdown && 'fd-popover__control',
          )}
          title={tooltip}
        >
          {isDropdown ? (
            <button
              aria-disabled={disabled}
              aria-label={ariaLabel || label}
              className="fd-navigation__link"
              disabled={disabled}
              onClick={onClick}
              onKeyDown={(e: React.KeyboardEvent) => keyPressHandler(e)}
              type="button"
            >
              {innerContent}
              <div className={styles['icon-container']}>
                <Icon
                  aria-hidden="true"
                  aria-label="has children"
                  name="megamenu"
                  role="presentation"
                />
              </div>
            </button>
          ) : (
            <a
              aria-disabled={disabled}
              aria-label={ariaLabel || label}
              className={classNames('fd-navigation__link', disabled && 'disabled')}
              href={href}
              onClick={onClick}
              onKeyDown={(e: React.KeyboardEvent) => keyPressHandler(e)}
              tabIndex={0}
            >
              {innerContent}
            </a>
          )}
        </div>
        {isDropdown && dropdownOpen && (
          <div
            aria-hidden="false"
            className={`fd-navigation__list-container fd-popover__body fd-popover__body--no-arrow ${styles['sba-dropdown']} ${styles['dropdown-container']}`}
          >
            <div
              className={`fd-navigation__list-wrapper fd-popover__wrapper ${styles['dropdown-wrapper']}`}
            >
              <ul
                aria-roledescription="Navigation List - Child Items"
                className="fd-navigation__list fd-navigation__list--child-items"
                role="tree"
                tabIndex={-1}
              >
                {options}
              </ul>
            </div>
          </div>
        )}
      </li>
    );
  },
);
